

import React, { useState, useRef, useEffect } from "react";
import { Button, Alert, Badge } from "./ui";

const inputClass =
  "w-full px-4 py-3 border border-prodia-border rounded-lg focus:border-prodia-primary focus:ring-2 focus:ring-prodia-primary/30 outline-none transition-all bg-prodia-surface text-prodia-text-primary placeholder:text-prodia-text-muted";

// 日付入力コンポーネント（手入力+カレンダー対応、YYYY/MM/DD自動フォーマット）
function SmartDateInput({ value, onChange, name, className, style }) {
  const [displayValue, setDisplayValue] = useState("");
  const textInputRef = useRef(null);
  const hiddenDateRef = useRef(null);

  // 外部から value が変わったとき（初期値、フォームリセット）にdisplay同期
  useEffect(() => {
    if (value) {
      const parts = value.split("-");
      if (parts.length === 3) {
        setDisplayValue(`${parts[0]}/${parts[1]}/${parts[2]}`);
      } else {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue("");
    }
  }, [value]);

  const handleTextChange = (e) => {
    const raw = e.target.value;
    // 数字と"/"のみ許容
    const cleaned = raw.replace(/[^0-9/]/g, "");
    const digits = cleaned.replace(/\//g, "").slice(0, 8);

    // 桁数に応じて自動スラッシュ挿入
    let formatted = "";
    if (digits.length <= 4) {
      formatted = digits;
    } else if (digits.length <= 6) {
      formatted = `${digits.slice(0, 4)}/${digits.slice(4)}`;
    } else {
      formatted = `${digits.slice(0, 4)}/${digits.slice(4, 6)}/${digits.slice(6, 8)}`;
    }

    setDisplayValue(formatted);

    // 8桁揃ったら親フォームに ISO 形式で通知
    if (digits.length === 8) {
      const isoDate = `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
      onChange({ target: { name, value: isoDate } });
    } else if (digits.length === 0) {
      // 全消去した場合のみリセット
      onChange({ target: { name, value: "" } });
    }
    // 1～7桁の途中入力中は親に通知しない（入力中の値を保持）
  };

  // カーソルを常に末尾へ移動
  useEffect(() => {
    const el = textInputRef.current;
    if (el && document.activeElement === el) {
      const len = displayValue.length;
      el.setSelectionRange(len, len);
    }
  }, [displayValue]);

  // カレンダーで日付選択されたとき
  const handleCalendarChange = (e) => {
    const isoDate = e.target.value; // YYYY-MM-DD
    if (isoDate) {
      const parts = isoDate.split("-");
      setDisplayValue(`${parts[0]}/${parts[1]}/${parts[2]}`);
      onChange({ target: { name, value: isoDate } });
    }
  };

  const openCalendar = () => {
    if (hiddenDateRef.current) {
      try {
        hiddenDateRef.current.showPicker();
      } catch {
        hiddenDateRef.current.focus();
        hiddenDateRef.current.click();
      }
    }
  };

  return (
    <div className="relative">
      <input
        ref={textInputRef}
        type="text"
        value={displayValue}
        onChange={handleTextChange}
        className={`${className} pr-10`}
        style={style}
        placeholder="YYYY/MM/DD"
        maxLength={10}
        inputMode="numeric"
        autoComplete="off"
      />
      <button
        type="button"
        onClick={openCalendar}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-prodia-text-muted hover:text-prodia-text-secondary transition-colors"
        tabIndex={-1}
        aria-label="カレンダーで選択"
      >
        <i className="fas fa-calendar-alt"></i>
      </button>
      {/* 非表示のネイティブdateインプット（カレンダーUIのため） */}
      <input
        ref={hiddenDateRef}
        type="date"
        value={value || ""}
        onChange={handleCalendarChange}
        className="absolute opacity-0 pointer-events-none w-0 h-0 overflow-hidden"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
}

// 性別選択肢
const GENDER_OPTIONS = [
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' },
];

// 担当プランナー選択肢
const PLANNER_OPTIONS = [
  "温水",
  "瀬戸山",
  "上前",
  "岡田",
  "野田",
  "服部"
];

const SKILL_OPTIONS = [
  { genre: "フロントエンド", skills: [
    "React", "TypeScript", "JavaScript", "Vue.js", "Next.js", "Nuxt.js", "HTML", "CSS", "Sass", "Tailwind CSS", "jQuery"
  ] },
  { genre: "バックエンド", skills: [
    "Python", "Django", "Flask", "FastAPI", "Java", "Spring Boot", "C#", ".NET", "PHP", "Laravel", "Ruby", "Ruby on Rails", "Node.js", "Express"
  ] },
  { genre: "クラウド/インフラ", skills: [
    "AWS", "GCP", "Azure", "Heroku", "Firebase", "Vercel", "Netlify", "Docker", "Kubernetes", "Linux", "Nginx", "Apache"
  ] },
  { genre: "データベース", skills: [
    "PostgreSQL", "MySQL", "SQLite", "MongoDB", "Redis", "Oracle", "DynamoDB"
  ] },
  { genre: "その他", skills: [
    "Figma", "Photoshop", "Illustrator", "Notion", "Slack", "Git", "REST API", "GraphQL"
  ] },
];
const PHASE_OPTIONS = [
  "要件定義", "基本設計", "詳細設計", "製造", "テスト", "運用・保守"
];

const formatRate = (v) => {
  const raw = String(v ?? "").replace(/[！-～]/g, c => String.fromCharCode(c.charCodeAt(0) - 0xFEE0)).replace(/　/g, ' ').replace(/,/g, "").replace(/[^0-9]/g, "");
  return raw ? Number(raw).toLocaleString() : "";
};

export default function EngineerForm({ onSubmit, onCancel, initialData }) {
  const [form, setForm] = useState(
    initialData
      ? {
          ...initialData,
          monthly_rate: formatRate(initialData.monthly_rate),
          project_locations: initialData.project_location
            ? initialData.project_location.split(", ").map(s => s.trim()).filter(Boolean)
            : [""],
        }
      : {
          name: "",
          gender: "",
          rate_type: "monthly",
          project_name: "",
          planner: "",
          skills: [],
          engineer_status: "",
          phase: [],
          client_company: "",
          monthly_rate: "",
          project_start_date: "",
          project_end_date: "",
          project_locations: [""],
        }
  );
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [customSkillInput, setCustomSkillInput] = useState("");
  const [customSkillError, setCustomSkillError] = useState("");

  // 定義済みスキル一覧（重複チェック用）
  const allDefinedSkills = SKILL_OPTIONS.flatMap(g => g.skills);

  const handleAddCustomSkill = () => {
    const trimmed = customSkillInput.trim();
    if (!trimmed) return;
    if (form.skills.includes(trimmed)) {
      setCustomSkillError("そのスキルはすでに追加されています");
      return;
    }
    setCustomSkillError("");
    setForm(f => ({ ...f, skills: [...f.skills, trimmed] }));
    setCustomSkillInput("");
  };

  const handleCustomSkillKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddCustomSkill();
    }
  };


  const handleChange = (e) => {
    // エラーメッセージをクリア
    if (error) setError("");
    if (successMessage) setSuccessMessage("");
    
    const { name, value, type, options } = e.target;
    if (type === "select-multiple") {
      const values = Array.from(options).filter(o => o.selected).map(o => o.value);
      setForm({ ...form, [name]: values });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleRateChange = (e) => {
    if (error) setError("");
    if (successMessage) setSuccessMessage("");
    setForm({ ...form, monthly_rate: formatRate(e.target.value) });
  };

  const handleSubmit = async (e, continueAfter = false) => {
    e.preventDefault();
    if (!form.name) {
      setError("名前は必須です");
      return;
    }
    
    setError("");
    setIsSubmitting(true);
    
    try {
      // 一意のメールアドレスを生成
      const timestamp = Date.now();
      const randomNum = Math.floor(Math.random() * 1000);
      const uniqueEmail = `engineer.${timestamp}.${randomNum}@prodia.temp`;
      
      await onSubmit({
        ...form,
        email: uniqueEmail,
        skills: form.skills,
        phase: form.phase,
        rate_type: form.rate_type || 'monthly',
        monthly_rate: form.monthly_rate ? form.monthly_rate.replace(/,/g, "") : "",
        settlement_lower: form.rate_type === 'monthly' ? (form.settlement_lower || null) : null,
        settlement_upper: form.rate_type === 'monthly' ? (form.settlement_upper || null) : null,
        project_start_date: form.project_start_date || null,
        project_end_date: form.project_end_date || null,
        project_location: form.project_locations.filter(v => v.trim()).join(", "),
      }, continueAfter);
      
      // 成功メッセージを表示
      setSuccessMessage(continueAfter ? "登録完了！続けて登録できます" : "登録完了！");
      
      // 連続登録の場合はフォームをリセット
      if (continueAfter) {
        setForm({
          name: "",
          gender: "",
          rate_type: "monthly",
          project_name: "",
          planner: "",
          skills: [],
          engineer_status: "",
          phase: [],
          client_company: "",
          monthly_rate: "",
          project_start_date: "",
          project_end_date: "",
          project_locations: [""],
        });
        
        // 成功メッセージを3秒後に消去
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      {/* 背景オーバーレイ */}
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px]" />

      {/* ===== 中央モーダル本体 ===== */}
      <form
        className="relative w-full max-w-2xl flex flex-col bg-prodia-surface border border-prodia-border rounded-xl overflow-hidden shadow-prodia-lg"
        style={{ animation: 'modalFadeIn 0.22s cubic-bezier(0.16,1,0.3,1)', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        {/* ===== 固定ヘッダー ===== */}
        <div className="flex-shrink-0 px-8 py-5 bg-prodia-surface border-b border-prodia-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-prodia-primary-subtle text-prodia-primary border border-amber-200">
              <i className={`fas ${initialData ? 'fa-edit' : 'fa-user-plus'}`}></i>
            </div>
            <div>
              <h2 className="text-xl font-bold leading-tight text-prodia-text-primary">
                {initialData ? 'IDRエンジニア編集' : 'IDRエンジニア新規登録'}
              </h2>
              <p className="text-xs text-prodia-text-secondary mt-0.5">
                {initialData ? 'IDRエンジニア情報を更新します' : '新しいIDRエンジニアを登録します'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-prodia-background hover:bg-danger-bg text-prodia-text-muted hover:text-danger transition-all"
            aria-label="閉じる"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* ===== スクロール可能なコンテンツ領域 ===== */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
        {error && (
          <Alert variant="danger" title={error} className="mb-6" />
        )}
        {successMessage && (
          <Alert variant="success" title={successMessage} className="mb-6" />
        )}
        {/* 基本情報セクション */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
            <i className="fas fa-user text-prodia-primary"></i>
            基本情報
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 名前 */}
            <div className="relative">
              <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                名前 <span className="text-danger">*</span>
              </label>
              <div className="relative">
                <input 
                  name="name" 
                  value={form.name} 
                  onChange={handleChange} 
                  required
                  autoComplete="off"
                  className={`${inputClass} text-lg font-medium pr-10`}
                  placeholder="田中太郎"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <i className="fas fa-user text-prodia-text-muted"></i>
                </div>
              </div>
            </div>
            {/* 性別 */}
            <div className="relative">
              <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                性別
              </label>
              <div className="relative">
                <select
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  className={`${inputClass} text-lg font-medium appearance-none cursor-pointer pr-12`}
                >
                  <option value="">選択してください</option>
                  {GENDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <i className="fas fa-user-circle text-prodia-text-muted"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* アサイン状況セクション */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
            <i className="fas fa-briefcase text-prodia-primary"></i>
            アサイン状況
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "アサイン済", label: "アサイン済", icon: "fa-check-circle", selected: "bg-success text-white border-success", unselected: "bg-prodia-surface text-success-foreground border-success-border hover:bg-success-bg" },
              { value: "未アサイン", label: "未アサイン", icon: "fa-clock", selected: "bg-warning text-white border-warning", unselected: "bg-prodia-surface text-warning-foreground border-warning-border hover:bg-warning-bg" },
            ].map(opt => (
              <button
                type="button"
                key={opt.value}
                className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                  form.engineer_status === opt.value ? opt.selected : opt.unselected
                }`}
                onClick={() => setForm(f => ({
                  ...f,
                  engineer_status: opt.value,
                  ...(opt.value === '未アサイン' ? {
                    project_name: '', planner: '', client_company: '',
                    monthly_rate: '', settlement_lower: '', settlement_upper: '',
                    project_start_date: '', project_end_date: '', project_locations: ['']
                  } : {})
                }))}
              >
                <div className="flex items-center justify-center gap-2">
                  <i className={`fas ${opt.icon}`}></i>
                  <span className="font-medium whitespace-nowrap">{opt.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        {/* プロジェクト情報セクション（アサイン済のみ表示） */}
        {form.engineer_status === 'アサイン済' && (
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
            <i className="fas fa-project-diagram text-prodia-primary"></i>
            プロジェクト情報
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* ① プロジェクト内容 */}
            <div className="relative">
              <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                プロジェクト内容
              </label>
              <div className="relative">
                <input
                  name="project_name"
                  value={form.project_name}
                  onChange={handleChange}
                  className={`${inputClass} text-lg font-medium pr-10`}
                  placeholder="ECサイト開発"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <i className="fas fa-briefcase text-prodia-text-muted"></i>
                </div>
              </div>
            </div>

            {/* ② クライアント先企業名 */}
            <div className="relative">
              <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                クライアント先企業名
              </label>
              <div className="relative">
                <input
                  name="client_company"
                  value={form.client_company}
                  onChange={handleChange}
                  className={`${inputClass} text-lg font-medium pr-10`}
                  placeholder="株式会社○○"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <i className="fas fa-building text-prodia-text-muted"></i>
                </div>
              </div>
            </div>

            {/* ③ プロジェクト所在地（複数入力） */}
            <div className="relative">
              <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                プロジェクト所在地
              </label>
              <div className="space-y-2">
                {form.project_locations.map((loc, idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={loc}
                        onChange={e => {
                          const updated = [...form.project_locations];
                          updated[idx] = e.target.value;
                          setForm(f => ({ ...f, project_locations: updated }));
                        }}
                        className={`${inputClass} text-lg font-medium pr-10`}
                        placeholder="例: 名古屋市中区 / フルリモート"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <i className="fas fa-map-marker-alt text-prodia-text-muted"></i>
                      </div>
                    </div>
                    {form.project_locations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, project_locations: f.project_locations.filter((_, i) => i !== idx) }))}
                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-danger-bg hover:bg-danger-border text-danger transition-all flex-shrink-0"
                        aria-label="削除"
                      >
                        <i className="fas fa-minus text-sm"></i>
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, project_locations: [...f.project_locations, ""] }))}
                  className="flex items-center gap-1.5 text-sm text-prodia-primary hover:text-prodia-primary-hover font-medium transition-colors mt-1"
                >
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-prodia-primary-subtle border border-amber-200 transition-colors">
                    <i className="fas fa-plus text-xs"></i>
                  </span>
                  所在地を追加
                </button>
              </div>
            </div>

            {/* ④ 担当プランナー */}
            <div className="relative">
              <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                担当プランナー
              </label>
              <div className="relative">
                <select
                  name="planner"
                  value={form.planner}
                  onChange={handleChange}
                  className={`${inputClass} text-lg font-medium appearance-none cursor-pointer pr-12`}
                >
                  <option value="">選択してください</option>
                  {PLANNER_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <i className="fas fa-chevron-down text-prodia-text-muted"></i>
                </div>
              </div>
            </div>

            {/* ⑤ 参画開始日 */}
            <div className="relative">
              <label className="flex items-center min-h-[1.75rem] text-sm font-medium text-prodia-text-secondary mb-2">
                参画開始日
              </label>
              <SmartDateInput
                name="project_start_date"
                value={form.project_start_date}
                onChange={handleChange}
                className={`${inputClass} text-lg font-medium`}
              />
            </div>

            {/* ⑥ 契約終了予定日 */}
            <div className="relative">
              <label className="flex items-center gap-1.5 min-h-[1.75rem] text-sm font-medium text-prodia-text-secondary mb-2">
                契約終了予定日
                <span className="text-[11px] text-warning-foreground bg-warning-bg border border-warning-border px-1.5 py-0.5 rounded font-normal">アラート対象</span>
                <span className="text-prodia-text-muted cursor-default" title="設定すると稼働率ダッシュボードのアラートに表示されます">
                  <i className="fas fa-info-circle text-[11px]"></i>
                </span>
              </label>
              <SmartDateInput
                name="project_end_date"
                value={form.project_end_date}
                onChange={handleChange}
                className={`${inputClass} text-lg font-medium focus:border-warning focus:ring-warning/30`}
              />
            </div>

            {/* ⑦ 単価（フル幅） */}
            <div className="relative md:col-span-2">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-prodia-text-secondary">
                  {form.rate_type === 'monthly' ? '月単価' : '時給単価'}
                </label>
                {/* スライドトグル */}
                <div
                  className="relative flex bg-prodia-background border border-prodia-border rounded-full p-1 w-36 cursor-pointer select-none"
                  onClick={() => setForm(f => ({ ...f, rate_type: f.rate_type === 'monthly' ? 'hourly' : 'monthly', monthly_rate: '', settlement_lower: '', settlement_upper: '' }))}
                >
                  <div className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-prodia-surface rounded-full shadow-prodia transition-all duration-300 ${form.rate_type === 'monthly' ? 'left-1' : 'left-[calc(50%+3px)]'}`}></div>
                  <span className={`relative z-10 flex-1 text-center text-xs font-semibold py-1 transition-colors duration-300 ${form.rate_type === 'monthly' ? 'text-prodia-primary' : 'text-prodia-text-muted'}`}>月単価</span>
                  <span className={`relative z-10 flex-1 text-center text-xs font-semibold py-1 transition-colors duration-300 ${form.rate_type === 'hourly' ? 'text-prodia-primary' : 'text-prodia-text-muted'}`}>時給単価</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-prodia-text-secondary font-medium text-lg flex-shrink-0">¥</span>
                <input
                  type="text"
                  inputMode="numeric"
                  name="monthly_rate"
                  value={form.monthly_rate}
                  onChange={handleRateChange}
                  className={`${inputClass} text-lg font-medium flex-1`}
                  placeholder={form.rate_type === 'monthly' ? '650,000' : '3,000'}
                />
                {form.rate_type === 'hourly' && (
                  <span className="text-prodia-text-secondary font-medium text-base flex-shrink-0">/h</span>
                )}
              </div>

              {/* 精算幅（月単価のみ表示） */}
              {form.rate_type === 'monthly' && (
                <div className="mt-4 grid grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                      時間幅（下限）
                    </label>
                    <div className="relative flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.settlement_lower}
                        onChange={(e) => setForm(f => ({ ...f, settlement_lower: e.target.value.replace(/[^0-9]/g, '') }))}
                        className={`${inputClass} text-lg font-medium`}
                        placeholder="140"
                      />
                      <span className="text-prodia-text-secondary font-medium text-base shrink-0">h</span>
                    </div>
                  </div>
                  <div className="relative">
                    <label className="block text-sm font-medium text-prodia-text-secondary mb-2">
                      時間幅（上限）
                    </label>
                    <div className="relative flex items-center gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        value={form.settlement_upper}
                        onChange={(e) => setForm(f => ({ ...f, settlement_upper: e.target.value.replace(/[^0-9]/g, '') }))}
                        className={`${inputClass} text-lg font-medium`}
                        placeholder="180"
                      />
                      <span className="text-prodia-text-secondary font-medium text-base shrink-0">h</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
        )}
        {/* スキル選択セクション */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
            <i className="fas fa-code text-prodia-primary"></i>
            技術スキル
            <span className="text-sm font-normal text-prodia-text-secondary">（複数選択可）</span>
          </h3>
          
          <div className="bg-prodia-background p-6 rounded-xl border border-prodia-border">
            <div className="space-y-6">
              {SKILL_OPTIONS.map((group) => (
                <div key={group.genre}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 bg-prodia-primary rounded-full"></div>
                    <h4 className="text-sm font-semibold text-prodia-text-primary uppercase tracking-wide">
                      {group.genre}
                    </h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.skills.map((s) => (
                      <button
                        type="button"
                        key={s}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                          form.skills.includes(s) 
                            ? 'bg-prodia-primary text-prodia-primary-foreground border border-prodia-primary' 
                            : 'bg-prodia-surface text-prodia-text-secondary border border-prodia-border hover:border-prodia-primary hover:bg-prodia-primary-subtle'
                        }`}
                        onClick={() => {
                          setForm(f => f.skills.includes(s) ? { ...f, skills: f.skills.filter(x => x !== s) } : { ...f, skills: [...f.skills, s] });
                        }}
                      >
                        {form.skills.includes(s) && <i className="fas fa-check mr-1"></i>}
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-prodia-surface rounded-xl border border-prodia-border">
              <p className="text-sm font-semibold text-prodia-text-primary mb-2">
                <i className="fas fa-plus-circle mr-1 text-prodia-primary"></i>
                リストにないスキルを追加
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customSkillInput}
                  onChange={e => { setCustomSkillInput(e.target.value); setCustomSkillError(""); }}
                  onKeyDown={handleCustomSkillKeyDown}
                  className="flex-1 px-3 py-2 border border-prodia-border rounded-lg text-sm focus:border-prodia-primary focus:ring-2 focus:ring-prodia-primary/30 outline-none transition-all bg-prodia-surface placeholder:text-prodia-text-muted"
                  placeholder="例: Kotlin, Terraform, Figma..."
                  maxLength={50}
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={handleAddCustomSkill}
                  disabled={!customSkillInput.trim()}
                >
                  追加
                </Button>
              </div>
              {customSkillError && (
                <p className="text-xs text-danger mt-1">{customSkillError}</p>
              )}
              {form.skills.filter(s => !allDefinedSkills.includes(s)).length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-prodia-text-muted mb-1">追加済みのカスタムスキル:</p>
                  <div className="flex flex-wrap gap-2">
                    {form.skills.filter(s => !allDefinedSkills.includes(s)).map(s => (
                      <Badge key={s} variant="primary" icon="fas fa-star" className="pr-1">
                        {s}
                        <button
                          type="button"
                          onClick={() => setForm(f => ({ ...f, skills: f.skills.filter(x => x !== s) }))}
                          className="ml-1 text-prodia-primary/70 hover:text-prodia-primary transition-colors"
                          aria-label={`${s}を削除`}
                        >
                          <i className="fas fa-times text-xs"></i>
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3 p-3 bg-prodia-surface rounded-xl border border-prodia-border">
              <div className="flex items-center justify-between">
                <span className="text-sm text-prodia-text-secondary font-medium">
                  選択中のスキル: {form.skills.length}個
                </span>
                {form.skills.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, skills: [] }))}
                    className="text-xs text-prodia-primary hover:text-prodia-primary-hover underline"
                  >
                    すべてクリア
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* ステータス情報セクション */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-prodia-text-primary mb-4 flex items-center gap-2">
            <i className="fas fa-chart-line text-prodia-primary"></i>
            ステータス情報
          </h3>
          
          <div className="grid grid-cols-1 gap-6">
            <div>
              <label className="block text-sm font-medium text-prodia-text-secondary mb-3">経験フェーズ（複数選択可）</label>
              <div className="bg-prodia-background p-4 rounded-xl border border-prodia-border">
                <div className="grid grid-cols-2 gap-2">
                  {PHASE_OPTIONS.map((p) => (
                    <button
                      type="button"
                      key={p}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        form.phase.includes(p) 
                          ? 'bg-prodia-primary text-prodia-primary-foreground border border-prodia-primary' 
                          : 'bg-prodia-surface text-prodia-text-secondary border border-prodia-border hover:border-prodia-primary hover:bg-prodia-primary-subtle'
                      }`}
                      onClick={() => {
                        setForm(f => f.phase.includes(p) ? { ...f, phase: f.phase.filter(x => x !== p) } : { ...f, phase: [...f.phase, p] });
                      }}
                    >
                      {form.phase.includes(p) && <i className="fas fa-check mr-1"></i>}
                      {p}
                    </button>
                  ))}
                </div>
                
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-prodia-text-secondary font-medium">
                    選択中: {form.phase.length}個
                  </span>
                  {form.phase.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setForm(f => ({ ...f, phase: [] }))}
                      className="text-xs text-prodia-primary hover:text-prodia-primary-hover underline"
                    >
                      クリア
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>{/* ===== スクロール可能なコンテンツ領域 終了 ===== */}

        {/* ===== 固定フッター（アクションボタン） ===== */}
        <div className="flex-shrink-0 px-8 py-5 bg-prodia-surface border-t border-prodia-border">
          <div className="flex flex-col sm:flex-row gap-3 justify-end">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              <i className="fas fa-times"></i>
              キャンセル
            </Button>

            {!initialData && (
              <Button
                type="button"
                variant="secondary"
                onClick={(e) => handleSubmit(e, true)}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <><i className="fas fa-spinner fa-spin"></i>登録中...</>
                ) : (
                  <><i className="fas fa-plus-circle"></i>登録して続ける</>
                )}
              </Button>
            )}

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <><i className="fas fa-spinner fa-spin"></i>{initialData ? "更新中..." : "登録中..."}</>
              ) : (
                <><i className={`fas ${initialData ? 'fa-save' : 'fa-check'}`}></i>{initialData ? "更新して完了" : "登録して完了"}</>
              )}
            </Button>
          </div>
          <p className="text-xs text-prodia-text-muted mt-3 text-center">
            <i className="fas fa-info-circle mr-1"></i>
            必須項目（<span className="text-danger">*</span>）は入力必須です
          </p>
        </div>
      </form>
    </div>
  );
}
